import { useState } from "react";
import toast from "react-hot-toast";
import { FileText, Download } from "lucide-react";
import { useMyDocuments, downloadMyDocument } from "@/api/portal";
import { DOCUMENT_CATEGORY_LABEL, formatFileSize } from "@/api/documents";
import { getApiErrorMessage } from "@/api/client";

export default function PortalDocumentsPage() {
  const { data, isLoading } = useMyDocuments({ pageSize: 100 });
  const documents = data?.data ?? [];
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (documentId: string) => {
    setDownloadingId(documentId);
    try {
      const { url } = await downloadMyDocument(documentId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Documentos</h1>
        <p className="text-sm text-gray-500">Documentos que la firma ha cargado para usted.</p>
      </div>

      <div className="card p-6">
        {isLoading && <p className="text-sm text-gray-400">Cargando documentos...</p>}
        {!isLoading && documents.length === 0 && (
          <p className="text-sm text-gray-400">Aún no tiene documentos cargados.</p>
        )}
        {!isLoading && documents.length > 0 && (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                    <FileText size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {doc.originalName}
                      {doc.version > 1 && <span className="ml-1 text-xs font-normal text-gray-400">v{doc.version}</span>}
                    </p>
                    <p className="text-xs text-gray-500">
                      {DOCUMENT_CATEGORY_LABEL[doc.category]} · {formatFileSize(doc.sizeBytes)}
                      {doc.expirationDate && <span className="ml-1">· Vence {doc.expirationDate}</span>}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded p-1.5 text-gray-400 hover:bg-gray-50 hover:text-brand-600 disabled:opacity-50"
                  title="Descargar"
                  onClick={() => handleDownload(doc.id)}
                  disabled={downloadingId === doc.id}
                >
                  <Download size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
